import { BranchType } from "@ibada/shared-types/common"
import { FinanceCategoryScope, FinanceTransferStatus } from "@/features/finance/model/finance-types"

/**
 * What a person can do to a transfer from the transfers table.
 *
 * A transfer is a two-sided agreement, so the answer depends on WHICH SIDE is looking as much as on the
 * status: the sender submits and cancels, the receiver approves and rejects, and neither can do the other's
 * half. Keeping that as a pure function rather than inline JSX means the rules can be tested against the
 * backend's guards directly — and those guards are the real authority, so a mismatch here shows up as a 409
 * the user cannot act on rather than as a missing button.
 */
export enum TransferAction {
  Edit = "EDIT",
  Submit = "SUBMIT",
  Approve = "APPROVE",
  Reject = "REJECT",
  Cancel = "CANCEL",
  Reverse = "REVERSE",
}

/**
 * The actions to offer, in lifecycle order.
 *
 * Mirrors `CrossContextTransfersService`:
 * - `submit` / `update` → DRAFT, source side (`TRANSFER_NOT_DRAFT`)
 * - `approve` / `reject` → PENDING, destination side (`TRANSFER_NOT_PENDING`)
 * - `cancel` → DRAFT or PENDING, source side (`TRANSFER_NOT_CANCELLABLE`)
 * - `reverse` → APPROVED and not already reversed (`TRANSFER_NOT_REVERSIBLE`)
 *
 * Reversal is offered from EITHER side because the backend requires authorisation on BOTH — showing it only
 * to one side would hide the only correction path from the other, and the request is refused server-side
 * anyway if the viewer lacks the far side.
 */
export function availableTransferActions(input: {
  status: FinanceTransferStatus
  /** Is the branch being viewed the one SENDING the money? */
  isSource: boolean
  /** Is it the one RECEIVING? */
  isDestination: boolean
  /** Already reversed transfers are done — a second reversal would double the correction. */
  isReversed?: boolean
  /** Finance EDIT permission. Without it the row is read-only, whichever side it is. */
  canEdit: boolean
}): TransferAction[] {
  if (!input.canEdit) return []

  const actions: TransferAction[] = []

  if (input.status === FinanceTransferStatus.Draft && input.isSource) {
    actions.push(TransferAction.Edit, TransferAction.Submit)
  }

  if (input.status === FinanceTransferStatus.Pending && input.isDestination) {
    actions.push(TransferAction.Approve, TransferAction.Reject)
  }

  if (
    (input.status === FinanceTransferStatus.Draft || input.status === FinanceTransferStatus.Pending) &&
    input.isSource
  ) {
    actions.push(TransferAction.Cancel)
  }

  if (input.status === FinanceTransferStatus.Approved && !input.isReversed && (input.isSource || input.isDestination)) {
    actions.push(TransferAction.Reverse)
  }

  return actions
}

/** Actions that destroy or undo something, so they render red and confirm before running. */
export function isDestructiveTransferAction(action: TransferAction): boolean {
  return (
    action === TransferAction.Reject || action === TransferAction.Cancel || action === TransferAction.Reverse
  )
}

/**
 * The scope to query a branch's accounts and funds under.
 *
 * The system branch owns its finance directory at PARISH scope, and the list endpoints reject the mismatch
 * outright — "Use PARISH scope for system-owned funds" — rather than returning an empty list. So this is not
 * a preference: asking under the wrong scope is a 400, and for the parish that meant an approval dialog with
 * two empty pickers and no way to complete the approval.
 */
export function financeScopeForBranchType(branchType: BranchType | undefined | null): FinanceCategoryScope {
  return branchType === BranchType.System ? FinanceCategoryScope.Parish : FinanceCategoryScope.Branch
}
